import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import {
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { translate } from '@jsverse/transloco';
import { Subject, takeUntil } from 'rxjs';
import { DropdownObject } from '@inmoment/the-kitchen';

import { ChartOptionConstants, Constants } from 'app/constants/constants';
import { ChartDisplayOptions } from '../../../../models';
import {
  BoxScoringDisplayData,
  BoxScoringDisplayDataUtils,
} from '../box-scoring-display/box-scoring-display.component';
import { WidgetChartOptions } from '../widget-chart/widget-chart.component';

@Component({
  selector: 'app-donut-chart-options-dialog',
  templateUrl: './donut-chart-options-dialog.component.html',
  styleUrls: ['./donut-chart-options-dialog.component.scss'],
  standalone: false,
})
export class DonutChartOptionsDialogComponent implements OnInit, OnChanges, OnDestroy {
  @Input() public data: ChartDisplayOptions;

  @Output() modalClosed: EventEmitter<ChartDisplayOptions> =
    new EventEmitter<ChartDisplayOptions>();

  form: UntypedFormGroup;

  boxScoringDisplayData: BoxScoringDisplayData = BoxScoringDisplayDataUtils.getObjectDefaults();
  isBoxScoringDisplayDataValid = true;

  private unsub$: Subject<void> = new Subject<void>();

  readonly boxTypeSet: DropdownObject[] = [
    { title: translate('widgets.options.top'), id: 'top' },
    { title: translate('widgets.options.bottom'), id: 'bottom' },
  ];

  readonly scoringSet: DropdownObject[] = [
    { title: translate('widgets.options.rating'), id: 'rating' },
    { title: translate('widgets.options.box'), id: 'box' },
  ];

  boxSizeSet: DropdownObject[];

  readonly comparisonColorRadios = [
    {
      label: translate('widgets.options.colors.gray'),
      value: 'gray',
    },
    {
      label: translate('widgets.options.colors.red'),
      value: 'red',
    },
    {
      label: translate('widgets.options.colors.green'),
      value: 'green',
    },
  ];

  decimalPrecisionSet: DropdownObject[];

  constructor(formBuilder: UntypedFormBuilder) {
    this.configForm(formBuilder);

    this.boxSizeSet = ChartOptionConstants.TOPBOXSIZEDOS;
    this.decimalPrecisionSet = ChartOptionConstants.DECIMALPRECISIONDOS;
  }

  ngOnInit(): void {
    this.loadForm();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.data && this.data?.defaultColorPalette) {
      this.boxScoringDisplayData.defaultColorPalette = this.data.defaultColorPalette;
    }
  }

  ngOnDestroy(): void {
    this.unsub$.next();
    this.unsub$.complete();
  }

  onSave(): void {
    const result = this.createResult();

    this.modalClosed.emit(result);
  }

  onCancel(): void {
    this.modalClosed.emit();
  }

  onBoxScoringDisplayChanged(data: BoxScoringDisplayData): void {
    this.boxScoringDisplayData = data;

    this.isBoxScoringDisplayDataValid =
      data.isTopShown || data.isNeutralShown || data.isBottomShown;
  }

  private configForm(formBuilder: UntypedFormBuilder): void {
    this.form = formBuilder.group({
      metric: new UntypedFormControl('avg', Validators.required),
      metricBoxType: new UntypedFormControl({ value: 'top', disabled: true }),
      metricBoxSize: new UntypedFormControl({ value: '1', disabled: true }),
      minRating: new UntypedFormControl(0, Validators.required),
      maxRating: new UntypedFormControl(10, [
        Validators.required,
        Validators.min(2),
        Validators.max(1000),
      ]),
      decimalPrecision: new UntypedFormControl('2', Validators.required),
      increaseColor: new UntypedFormControl('gray', Validators.required),
      decreaseColor: new UntypedFormControl('gray', Validators.required),
      showAs: new UntypedFormControl('absolute', Validators.required),
      scoring: new UntypedFormControl('rating'),
    });

    this.form.controls.metric.valueChanges.pipe(takeUntil(this.unsub$)).subscribe((v) => {
      if (v === 'box') {
        this.form.controls.metricBoxType.enable({ onlySelf: true, emitEvent: false });
        this.form.controls.metricBoxSize.enable({ onlySelf: true, emitEvent: false });
      } else {
        this.form.controls.metricBoxType.disable({ onlySelf: true, emitEvent: false });
        this.form.controls.metricBoxSize.disable({ onlySelf: true, emitEvent: false });
      }
    });

    this.form.controls.metricBoxType.valueChanges.pipe(takeUntil(this.unsub$)).subscribe((v) => {
      if (v === 'bottom') {
        this.boxSizeSet = ChartOptionConstants.BOTTOMBOXSIZEDOS;
      } else {
        this.boxSizeSet = ChartOptionConstants.TOPBOXSIZEDOS;
      }

      this.form.controls.metricBoxSize.setValue(Constants.TWTRA_DEFAULT_BOX_SIZE);
    });
  }

  private loadForm(): void {
    const donutOptions = (this.data.payload as WidgetChartOptions).donutOptions;

    this.form.patchValue({
      metric: donutOptions.metric,
      minRating: donutOptions.minRating,
      maxRating: donutOptions.maxRating,
      decimalPrecision: (
        donutOptions.decimalPrecision ?? ChartOptionConstants.DEFAULT_DECIMAL_PRECISION
      ).toString(),
      increaseColor: donutOptions.increaseColor,
      decreaseColor: donutOptions.decreaseColor,
      showAs: donutOptions.showAs,
      scoring: donutOptions.scoring,
    });

    if (donutOptions.metric === 'box') {
      this.form.patchValue({
        metricBoxType: donutOptions.metricBoxType,
        metricBoxSize: donutOptions.metricBoxSize,
      });
    }

    const { topColor, neutralColor, bottomColor } = BoxScoringDisplayDataUtils.getObjectDefaults();

    this.boxScoringDisplayData = {
      ...this.boxScoringDisplayData,
      defaultColorPalette: this.data.defaultColorPalette,
      topColor: donutOptions.topColor ?? topColor,
      neutralColor: donutOptions.neutralColor ?? neutralColor,
      bottomColor: donutOptions.bottomColor ?? bottomColor,
    };

    if (donutOptions.scoring === 'box') {
      this.boxScoringDisplayData = {
        ...this.boxScoringDisplayData,
        isTopShown: donutOptions.isTopShown,
        isNeutralShown: donutOptions.isNeutralShown,
        isBottomShown: donutOptions.isBottomShown,
        topBoxSize: donutOptions.topBoxSize,
        bottomBoxSize: donutOptions.bottomBoxSize,
      };
    }
  }

  private createResult(): ChartDisplayOptions {
    const result = { ...this.data };
    result.needRefresh = true;

    result.payload.donutOptions = {
      metric: this.form.controls.metric.value,
      minRating: this.form.controls.minRating.value,
      maxRating: this.form.controls.maxRating.value,
      decimalPrecision: parseInt(this.form.controls.decimalPrecision.value, 10),
      increaseColor: this.form.controls.increaseColor.value,
      decreaseColor: this.form.controls.decreaseColor.value,
      showAs: this.form.controls.showAs.value,
      scoring: this.form.controls.scoring.value,
      ...this.boxScoringDisplayData,
    };

    if (result.payload.donutOptions.metric === 'box') {
      result.payload.donutOptions.metricBoxType = this.form.controls.metricBoxType.value;
      result.payload.donutOptions.metricBoxSize = this.form.controls.metricBoxSize.value;
    }

    if (result.payload.donutOptions.scoring === 'box') {
      result.payload.donutOptions = {
        ...result.payload.donutOptions,
        ...this.boxScoringDisplayData,
      };
    }
    return result;
  }
}
